import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import modulosService, { ModuloMasterDto } from '../../services/modulosService';
import { getApiErrorMessage } from '../../utils/apiError';
import ModuloLogo from './ModuloLogo';
import { SvgIconComponent } from '@mui/icons-material';

const MAX_SVG_BYTES = 200 * 1024;

interface ModuloAdminSectionProps {
  campo: string;
  icon: SvgIconComponent;
  color: string;
  master?: ModuloMasterDto;
  onMasterChange: (next: ModuloMasterDto) => void;
}

const ModuloAdminSection: React.FC<ModuloAdminSectionProps> = ({
  campo,
  icon,
  color,
  master,
  onMasterChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [costo, setCosto] = useState('');
  const [moneda, setMoneda] = useState<'ARS' | 'USD'>('ARS');
  const [savingCosto, setSavingCosto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setCosto(master?.costoMensual != null ? String(master.costoMensual) : '');
    setMoneda((master?.moneda?.toUpperCase() === 'USD' ? 'USD' : 'ARS'));
  }, [master?.costoMensual, master?.moneda, campo]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleSaveCosto = async () => {
    const trimmed = costo.trim();
    let costoMensual: number | null = null;
    if (trimmed !== '') {
      const parsed = Number(trimmed.replace(',', '.'));
      if (Number.isNaN(parsed) || parsed < 0) {
        enqueueSnackbar('Ingrese un costo mensual válido (mayor o igual a 0)', { variant: 'warning' });
        return;
      }
      costoMensual = parsed;
    }

    setSavingCosto(true);
    try {
      const next = await modulosService.updateCosto(campo, {
        costoMensual,
        moneda: costoMensual != null ? moneda : null,
      });
      onMasterChange(next);
      enqueueSnackbar('Costo mensual actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar el costo'), { variant: 'error' });
    } finally {
      setSavingCosto(false);
    }
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file) return;
    const nameOk = file.name.toLowerCase().endsWith('.svg');
    const typeOk = !file.type || file.type.includes('svg') || file.type === 'application/octet-stream';
    if (!nameOk && !typeOk) {
      enqueueSnackbar('Solo se aceptan archivos SVG', { variant: 'warning' });
      return;
    }
    if (file.size > MAX_SVG_BYTES) {
      enqueueSnackbar('El SVG supera el máximo de 200 KB', { variant: 'warning' });
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));

    setUploading(true);
    try {
      const next = await modulosService.uploadLogo(campo, file);
      onMasterChange(next);
      enqueueSnackbar('Logo actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo subir el logo'), { variant: 'error' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    setDeleting(true);
    try {
      const next = await modulosService.deleteLogo(campo);
      onMasterChange(next);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      enqueueSnackbar('Se restauró el ícono por defecto', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar el logo'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: '#fafafa',
        border: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1976d2' }}>
        Costo
      </Typography>

      <Stack spacing={2}>
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Costo mensual"
              size="small"
              type="number"
              value={costo}
              onChange={(e) => setCosto(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id={`moneda-${campo}`}>Moneda</InputLabel>
              <Select
                labelId={`moneda-${campo}`}
                label="Moneda"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value as 'ARS' | 'USD')}
              >
                <MenuItem value="ARS">ARS</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="medium"
              startIcon={savingCosto ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={savingCosto}
              onClick={handleSaveCosto}
              sx={{ height: 40, flexShrink: 0 }}
            >
              Guardar costo
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Dejar vacío para quitar el costo
          </Typography>
        </Box>

        <Divider />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Vista previa"
              sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 2, bgcolor: '#fff', p: 0.5 }}
            />
          ) : (
            <ModuloLogo
              campo={campo}
              tieneLogo={Boolean(master?.tieneLogo)}
              logoVersion={master?.logoVersion}
              icon={icon}
              color={color}
              size={64}
            />
          )}
          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              disabled={uploading || deleting}
              onClick={() => fileInputRef.current?.click()}
            >
              Subir SVG
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
              disabled={uploading || deleting || !master?.tieneLogo}
              onClick={handleDeleteLogo}
            >
              Restaurar ícono
            </Button>
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/svg+xml,.svg"
            hidden
            onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Solo archivos SVG de hasta 200 KB. El servidor elimina scripts y atributos peligrosos.
        </Typography>
      </Stack>
    </Box>
  );
};

export default ModuloAdminSection;
